import React, { memo, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import type { GridApi } from 'ag-grid-community';

import BasicSection from '../../components/display/layout/basicSection';
import FormulasTable from '../../components/formulas/table/FormulasTable';
import CompactTabMenu from '../../components/nav/CompactTabMenu/CompactTabMenu';

import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';
import routes from '../../constants/routes';

import { BaseError } from '../../helpers/BaseError';
import { getFormulaSchedulingType } from '../../helpers/formulaHelpers';

import useQueryEmailTemplates from '../../hooks/EmailTemplate/useQueryFetchTemplates';
import { useGetFormulaChannelTabsData } from '../../hooks/formulas/useGetFormulaChannelTabsData';
import { useDrawerNavigation } from '../../hooks/helper/useDrawerNavigation';
import useFormulaTimingSlots from '../../hooks/timing-slots/useTimingSlots';
import { useAuthSession } from '../../hooks/useAuthSession';
import { useFormulasCatalogState } from './hooks/useFormulasCatalogState';

import {
    FormulaCategory,
    FormulaDetailModel,
    FormulaSchedulingOptions,
    FormulaStatusData,
    FormulaTypesData,
} from '../../models/formula.model';
import { TimingSlotsModel } from '../../models/timing-slots.model';

import { FormulasCatalogModals } from './FormulasCatalogModals';
import FormulaDrawer from './formulaDrawer/FormulaDrawer';

interface FormulasCatalogProps {
    messages?: FormulaDetailModel[];
    isLoading?: boolean;
    isError?: boolean;
    error?: BaseError | null;
    formulaType?: FormulaType;
    setFormulaType?: (value: FormulaType) => void;
    hasDeleteAccess: boolean;
    hasCreateFormulaAccess: boolean;
}

const FormulasCatalog: React.FC<React.PropsWithChildren<FormulasCatalogProps>> =
    ({
        messages,
        isError,
        error,
        isLoading,
        formulaType,
        setFormulaType,
        hasDeleteAccess,
        hasCreateFormulaAccess,
    }) => {
        const history = useHistory();
        const location = history.location;

        const { data: timingSlots } = useFormulaTimingSlots();
        const status = location.pathname.replace(
            `${routes.formulasMain}/`,
            '',
        ) as FormulaCategory;

        const {
            drawer,
            testMessage,
            editModal,
            cloneModal,
            timeSlotsModal,
            refocusGrid,
            setGridApi,
            gridApi,
        } = useFormulasCatalogState();
        const { tabs } = useGetFormulaChannelTabsData(status);
        useAuthSession();

        const getEmptyStateText = useMemo(() => {
            if (formulaType) {
                return `You have no ${FormulaTypesData[
                    formulaType
                ].title.toLowerCase()} messages in ${FormulaStatusData[
                    status
                ]?.label.toUpperCase()} mode.`;
            } else {
                return 'You have no messages of this type available.';
            }
        }, [status, formulaType]);

        const drawerNavigation = useDrawerNavigation(
            messages ?? [],
            drawer.activeFormula,
            drawer.open,
        );

        const emailTemplateId =
            drawer.activeFormula?.additional_metadata?.email_template_id;

        const { data: emailTemplates } = useQueryEmailTemplates();

        const templateInfo = useMemo(() => {
            if (!emailTemplateId) return undefined;
            const template = emailTemplates?.find(
                (t) => t.email_template_id === emailTemplateId,
            );
            return {
                name: template?.template_name ?? emailTemplateId,
                isCustom: template?.is_aampe_custom_template ?? false,
            };
        }, [emailTemplateId, emailTemplates]);

        // Keep the previous template info while cycling through formulas
        // to avoid a flash of "No Template" between navigations
        const [stableTemplateInfo, setStableTemplateInfo] =
            useState(templateInfo);
        useEffect(() => {
            if (templateInfo !== undefined) {
                setStableTemplateInfo(templateInfo);
            } else if (!emailTemplateId) {
                setStableTemplateInfo(undefined);
            }
        }, [templateInfo, emailTemplateId]);

        const schedulingType = useMemo<FormulaSchedulingOptions>(
            () =>
                getFormulaSchedulingType(
                    timingSlots as TimingSlotsModel[],
                    {
                        ...drawer.activeFormula,
                        is_controlled_message:
                            drawer.activeFormula?.is_controlled_message,
                    } as FormulaDetailModel,
                ),
            [drawer.activeFormula, timingSlots],
        );

        return (
            <>
                <CompactTabMenu
                    activeTabKey={formulaType as string}
                    onTabClick={setFormulaType as (value: string) => void}
                    tabs={tabs}
                />
                <BasicSection
                    style={{
                        padding: aampeTheme.spacing4(6),
                    }}
                    belowTabs
                >
                    <FormulasTable
                        messages={messages ?? []}
                        emptyStateText={getEmptyStateText}
                        isLoading={isLoading}
                        isError={isError}
                        error={error}
                        formulaType={formulaType}
                        handleOpenDrawer={drawer.open}
                        openDrawerData={drawer.activeFormula}
                        selectFormulaToClone={cloneModal.select}
                        isClonePending={cloneModal.isPending}
                        onGridApiReady={setGridApi}
                        hasCreateFormulaAccess={hasCreateFormulaAccess}
                        hasDeleteAccess={hasDeleteAccess}
                        gridApi={gridApi as GridApi}
                    />

                    {drawer.activeFormula && (
                        <FormulaDrawer
                            isOpen={drawer.isOpen}
                            onClose={drawer.close}
                            formula={drawer.activeFormula}
                            selectFormulaToClone={cloneModal.select}
                            handleTestMessageOpen={testMessage.open}
                            openEditModalHandler={editModal.open}
                            setShowTimeSlotsModal={timeSlotsModal.open}
                            timingSlots={timingSlots}
                            schedulingType={schedulingType}
                            refocusGrid={refocusGrid}
                            hasDeleteAccess={hasDeleteAccess}
                            hasCreateFormulaAccess={hasCreateFormulaAccess}
                            navigation={drawerNavigation}
                            templateName={stableTemplateInfo?.name}
                            isCustomTemplate={stableTemplateInfo?.isCustom}
                        ></FormulaDrawer>
                    )}
                    {
                        <FormulasCatalogModals
                            activeFormula={drawer.activeFormula}
                            cloningFormulaId={cloneModal.cloningFormulaId}
                            clearCloningFormulaId={cloneModal.clear}
                            isClonePending={cloneModal.isPending}
                            submitClone={cloneModal.submit}
                            isTestMessageClick={testMessage.isOpen}
                            handleTestMessageClose={testMessage.close}
                            isEditModalOpen={editModal.isOpen}
                            editLiveMessageHandler={editModal.editLive}
                            closeEditModalHandler={editModal.close}
                            showTimeSlotsModal={timeSlotsModal.isOpen}
                            closeTimeSlotsModal={timeSlotsModal.close}
                            timingSlots={timingSlots}
                            schedulingType={schedulingType}
                        />
                    }
                </BasicSection>
            </>
        );
    };

export default memo(FormulasCatalog);
